import { useMemo } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import { METERS_TO_MILLIMETERS_FACTOR, localiseMeasurement } from '@/utils/unitSystem';
import { UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import { UNCERTAIN_PROFILE_SHORT_LABEL } from '@/constants';

type Props = {
  isExpanded: boolean;
  onChange: (event: React.SyntheticEvent, expanded: boolean) => void;
  governingValue: {
    height: number;
    estimatedWallLoss: number;
    estimatedWallLossPercentage: number;
    residualThickness: number;
    residualThicknessPercentage: number;
    residualCorrosionAllowance: number;
    residualCorrosionAllowancePercentage: number;
  };
  unitSystem: UnitSystemEnum;
};

/**
 * Governing value details presented in an expandable/collapsible accordion
 */
export const GoverningValueAccordion = ({
  isExpanded,
  onChange,
  governingValue,
  unitSystem,
}: Props) => {
  const governingValueDisplay = useMemo(() => {
    const {
      height,
      estimatedWallLoss,
      estimatedWallLossPercentage,
      residualThickness,
      residualThicknessPercentage,
      residualCorrosionAllowance,
      residualCorrosionAllowancePercentage,
    } = governingValue;
    const heightInMillimeters = height * METERS_TO_MILLIMETERS_FACTOR;
    const estimatedWallLossInMillimeters = estimatedWallLoss * METERS_TO_MILLIMETERS_FACTOR;
    const residualThicknessInMillimeters = residualThickness * METERS_TO_MILLIMETERS_FACTOR;
    const residualCorrosionAllowanceInMillimeters =
      residualCorrosionAllowance * METERS_TO_MILLIMETERS_FACTOR;
    return {
      height: localiseMeasurement({
        unitSystem,
        value: heightInMillimeters || undefined,
        sourceUnit: UnitsEnum.Meter,
        displayAsUnit: UnitsEnum.Millimeter,
        showUnitLabel: true,
        fallbackLabel: UNCERTAIN_PROFILE_SHORT_LABEL,
      }),
      estimatedWallLoss: localiseMeasurement({
        unitSystem,
        value: estimatedWallLossInMillimeters,
      }),
      estimatedWallLossPercentage: `${estimatedWallLossPercentage.toFixed(0)}%`,
      residualThickness: localiseMeasurement({
        unitSystem,
        value: residualThicknessInMillimeters,
      }),
      residualThicknessPercentage: `${residualThicknessPercentage.toFixed(0)}%`,
      residualCorrosionAllowance: localiseMeasurement({
        unitSystem,
        value: residualCorrosionAllowanceInMillimeters,
      }),
      residualCorrosionAllowancePercentage: `${residualCorrosionAllowancePercentage.toFixed(0)}%`,
    };
  }, [governingValue, unitSystem]);

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onChange}
      sx={{ '&.MuiAccordion-root::before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="governing-value-content"
        id="governing-value-header"
      >
        <Typography>
          <b>Governing value&nbsp;</b>
          (RCA&nbsp;
          {governingValueDisplay.residualCorrosionAllowancePercentage})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Typography>Height - {governingValueDisplay.height}</Typography>
        <Typography>
          Wall loss - {governingValueDisplay.estimatedWallLoss} (
          {governingValueDisplay.estimatedWallLossPercentage})
        </Typography>
        <Typography>
          Residual thickness - {governingValueDisplay.residualThickness} (
          {governingValueDisplay.residualThicknessPercentage})
        </Typography>
        <Typography>
          RCA - {governingValueDisplay.residualCorrosionAllowance} (
          {governingValueDisplay.residualCorrosionAllowancePercentage})
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};
